import { ApiError, apiService } from './apiService';

export interface BackendSettingRecord {
  id?: number;
  setting_key: string;
  setting_value: string;
  setting_type?: 'string' | 'number' | 'boolean' | 'json';
  category?: string;
  description?: string;
  updated_by?: string | number;
  updated_at?: string;
}

type SettingType = NonNullable<BackendSettingRecord['setting_type']>;

interface SaveSettingOptions {
  category?: string;
  description?: string;
  updatedBy?: string | number;
  type?: SettingType | 'text';
}

const parseSettingValue = (value: string, type?: SettingType) => {
  if (type === 'boolean') {
    return value === 'true' || value === '1';
  }

  if (type === 'number') {
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? value : numericValue;
  }

  if (type === 'json') {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  return value;
};

const inferSettingType = (value: unknown): SettingType => {
  if (typeof value === 'boolean') {
    return 'boolean';
  }

  if (typeof value === 'number') {
    return 'number';
  }

  if (value !== null && typeof value === 'object') {
    return 'json';
  }

  return 'string';
};

const normalizeSettingType = (type: SettingType | 'text'): SettingType => {
  return type === 'text' ? 'string' : type;
};

const serializeSettingValue = (value: unknown, type: SettingType): string => {
  if (type === 'json') {
    return JSON.stringify(value);
  }

  return String(value ?? '');
};

const getCurrentUserId = () => {
  try {
    const raw = localStorage.getItem('currentUser');
    if (!raw) return '';
    const user = JSON.parse(raw);
    return String(user?.id || '').trim();
  } catch {
    return '';
  }
};

const normalizeMySettingKey = (key: string) => {
  const normalizedKey = String(key || '').trim();
  const userId = getCurrentUserId();
  if (!userId || normalizedKey.startsWith(`user_${userId}_`)) return normalizedKey;
  return `user_${userId}_${normalizedKey}`;
};

const normalizeSharedSettingKey = (key: string) => {
  const normalizedKey = String(key || '').trim();
  return normalizedKey.startsWith('shared_') ? normalizedKey : `shared_${normalizedKey}`;
};

export const settingsService = {
  // Get all settings
  async getSettings(category?: string) {
    const query = category ? `?category=${encodeURIComponent(category)}` : '';
    return await apiService.get<BackendSettingRecord[]>(`/settings${query}`);
  },

  // Get setting by key
  async getSetting(key: string) {
    return await apiService.get<BackendSettingRecord>(`/settings/${encodeURIComponent(key)}`);
  },

  async getSettingValue<T>(key: string, fallbackValue: T): Promise<T> {
    try {
      const response = await this.getSetting(key);
      const setting = response.data;

      if (!setting) {
        return fallbackValue;
      }

      return parseSettingValue(setting.setting_value, setting.setting_type) as T;
    } catch (error) {
      const status = error instanceof ApiError ? error.status : (error as any)?.status;
      if (status === 404) {
        return fallbackValue;
      }

      throw error;
    }
  },

  async getPublicSettingValue<T>(key: string, fallbackValue: T): Promise<T> {
    try {
      const response = await apiService.get<BackendSettingRecord>(`/settings/public/${encodeURIComponent(key)}`);
      const setting = response.data;

      if (!setting) {
        return fallbackValue;
      }

      return parseSettingValue(setting.setting_value, setting.setting_type) as T;
    } catch (error) {
      const status = error instanceof ApiError ? error.status : (error as any)?.status;
      if (status === 404 || status === 403) {
        return fallbackValue;
      }

      throw error;
    }
  },

  async getMySettingValue<T>(key: string, fallbackValue: T): Promise<T> {
    try {
      const response = await apiService.get<BackendSettingRecord>(`/settings/self/${encodeURIComponent(normalizeMySettingKey(key))}`);
      const setting = response.data;

      if (!setting) {
        return fallbackValue;
      }

      return parseSettingValue(setting.setting_value, setting.setting_type) as T;
    } catch (error) {
      const status = error instanceof ApiError ? error.status : (error as any)?.status;
      if (status === 404 || status === 403) {
        return fallbackValue;
      }
      throw error;
    }
  },

  async getSharedSettingValue<T>(key: string, fallbackValue: T): Promise<T> {
    try {
      const response = await apiService.get<BackendSettingRecord>(`/settings/shared/${encodeURIComponent(normalizeSharedSettingKey(key))}`);
      const setting = response.data;

      if (!setting) {
        return fallbackValue;
      }

      return parseSettingValue(setting.setting_value, setting.setting_type) as T;
    } catch (error) {
      const status = error instanceof ApiError ? error.status : (error as any)?.status;
      if (status === 404 || status === 403) {
        return fallbackValue;
      }
      throw error;
    }
  },

  async saveMySetting(key: string, value: unknown, options: SaveSettingOptions = {}) {
    const type = normalizeSettingType(options.type || inferSettingType(value));
    const serializedValue = serializeSettingValue(value, type);

    return await apiService.put(`/settings/self/${encodeURIComponent(normalizeMySettingKey(key))}`, {
      value: serializedValue,
      type,
      category: options.category || 'mobile',
      description: options.description,
    });
  },

  async saveSharedSetting(key: string, value: unknown, options: SaveSettingOptions = {}) {
    const type = normalizeSettingType(options.type || inferSettingType(value));
    const serializedValue = serializeSettingValue(value, type);

    return await apiService.put(`/settings/shared/${encodeURIComponent(normalizeSharedSettingKey(key))}`, {
      value: serializedValue,
      type,
      category: options.category || 'ui_shared',
      description: options.description,
    });
  },

  // Update setting
  async updateSetting(key: string, value: unknown, updatedBy?: string | number, type?: string) {
    return await apiService.put(`/settings/${encodeURIComponent(key)}`, { value, updatedBy, type });
  },

  async createSetting(key: string, value: unknown, options: SaveSettingOptions = {}) {
    const type = normalizeSettingType(options.type || inferSettingType(value));

    return await apiService.post('/settings', {
      key,
      value: serializeSettingValue(value, type),
      type,
      category: options.category || 'general',
      description: options.description,
      updatedBy: options.updatedBy,
    });
  },

  async saveSetting(key: string, value: unknown, options: SaveSettingOptions = {}) {
    const type = normalizeSettingType(options.type || inferSettingType(value));
    const serializedValue = serializeSettingValue(value, type);

    try {
      return await this.updateSetting(key, serializedValue, options.updatedBy, type);
    } catch (error) {
      const status = error instanceof ApiError ? error.status : (error as any)?.status;
      if (status === 404) {
        return await this.createSetting(key, value, { ...options, type });
      }

      throw error;
    }
  },

  async sendTestEmail(toEmail: string) {
    return await apiService.post('/settings/send-test-email', { toEmail });
  },

  // Update multiple settings
  async updateSettings(settings: Array<{ key: string; value: unknown }>, updatedBy?: string | number) {
    return await apiService.put('/settings/bulk/update', {
      settings: settings.map((setting) => {
        const type = inferSettingType(setting.value);
        return {
          key: setting.key,
          value: serializeSettingValue(setting.value, type),
        };
      }),
      updatedBy,
    });
  },

  async getSettingsByPrefix(prefix: string) {
    return await apiService.get<BackendSettingRecord[]>(`/settings?category=optimization`);
  },
};
